import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { commandBarSearchPath } from "../lib/apiPaths";
import { EmptyState } from "./EmptyState";
import { Icon } from "./Icon";
import { Keycap } from "./Keycap";
import { SoftButton } from "./SoftButton";
import { StatusIndicator } from "./StatusIndicator";
import { Text } from "./Text";

type CommandBarSearch = {
  query: string;
  status: "searching" | "done" | "failed";
  results: string[];
};

export function useCommandBarSearch() {
  const [search, setSearch] = useState<CommandBarSearch | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const stop = () => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setSearch(null);
  };
  const start = (query: string) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setSearch({ query, status: "searching", results: [] });
    void fetch(commandBarSearchPath(), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(await response.text());
        return (await response.json()) as { results: string[] };
      })
      .then(
        ({ results }) => {
          if (!controller.signal.aborted)
            setSearch({ query, status: "done", results });
        },
        () => {
          if (!controller.signal.aborted)
            setSearch({ query, status: "failed", results: [] });
        }
      );
  };
  useEffect(() => () => controllerRef.current?.abort(), []);
  return { search, start, stop };
}

const Pill = styled("span")`
  display: flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
  white-space: nowrap;
`;

const ButtonLabel = styled("span")`
  display: flex;
  align-items: center;
  gap: 8px;
  --keycap-bg: var(--alpha8);
`;

function EnterButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  return (
    <SoftButton
      size="m"
      variant="secondary"
      style={{ paddingInline: 12 }}
      onClick={onPress}
    >
      <ButtonLabel>
        {label}
        <Keycap as="span">
          <Icon name="corner-down-left" size={12} />
        </Keycap>
      </ButtonLabel>
    </SoftButton>
  );
}

export function NoResults({ onSearch }: { onSearch: () => void }) {
  return (
    <EmptyState
      icon={<StatusIndicator state="resting" focused size={24} />}
      maxWidth={360}
      title="No results"
      description="3B can look for it for you."
      action={<EnterButton label="Ask 3B to find it" onPress={onSearch} />}
    />
  );
}

export function SearchStatus({
  search,
  onStop,
  onAskAgent,
}: {
  search: CommandBarSearch;
  onStop: () => void;
  onAskAgent: () => void;
}) {
  if (search.status === "searching") {
    return (
      <EmptyState
        icon={<StatusIndicator state="thinking" focused size={24} />}
        maxWidth={360}
        title="Searching…"
        description={`3B is looking for “${search.query}”.`}
        action={<EnterButton label="Stop" onPress={onStop} />}
      />
    );
  }
  return (
    <EmptyState
      icon={
        <StatusIndicator
          state={search.status === "failed" ? "error" : "resting"}
          focused
          size={24}
        />
      }
      maxWidth={360}
      title={search.status === "failed" ? "Search failed" : "No matches"}
      description={
        search.status === "failed"
          ? "3B couldn’t search right now."
          : "3B couldn’t find any matches."
      }
      action={<EnterButton label="Ask 3B in a chat" onPress={onAskAgent} />}
    />
  );
}

export function NotAddedPill() {
  return (
    <Pill>
      <Text variant="text-small-md">Not added</Text>
    </Pill>
  );
}
